import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Trans, t } from '@lingui/macro';
import { Alert, Checkbox } from '@economic/taco';

import { Field } from '~/app/shared/components/Form';
import SignupForm from '../../SignupForm';
import { useSignupDialogForm } from '../../../hooks/useSignupDialogForm';
import {
    USER_INFORMATION_STEP,
    USER_INFORMATION_STEP_MIXPANEL,
    trackSmartPayOnboarding,
    trackSmartPayOnboardingMixpanel,
} from '../../../../../tracking/tracking';

const validationSchema = formData => {
    const requiredErrorMessage = t`Required`;

    return Yup.object({
        email: Yup.string()
            .required(requiredErrorMessage)
            .email(() => t`Invalid email address`),
        emailConfirmed: Yup.string()
            .required(requiredErrorMessage)
            .email(() => t`Invalid email address`)
            .test('email-match', t`Emails do not match`, value => {
                return value === formData?.email;
            }),
        firstName: Yup.string().required(requiredErrorMessage),
        lastName: Yup.string().required(requiredErrorMessage),
        phone: Yup.string()
            .required(requiredErrorMessage)
            .matches(/^[+]{0,1}[\s0-9]*$/, t`Please set a valid phone number.`)
            .min(5, t`Please set a valid phone number.`)
            .max(25, t`Please set a valid phone number.`),
        newsletter: Yup.bool(),
    });
};

export const UserInformation = () => {
    const { formData, updateFormData } = useSignupDialogForm();

    const [hasUpdatedEmail, setHasUpdatedEmail] = useState(false);

    useEffect(() => {
        trackSmartPayOnboardingMixpanel(USER_INFORMATION_STEP_MIXPANEL);
        trackSmartPayOnboarding(USER_INFORMATION_STEP);
    }, []);

    const onNewsletterConsentChange = value => updateFormData({ newsletter: value });

    const onFormUpdate = () => [
        {
            field: 'email',
            updates: email => {
                // clear emailConfirmed if email is updated the first time
                if (!hasUpdatedEmail) {
                    setHasUpdatedEmail(true);
                    return email !== formData?.email ? { emailConfirmed: '' } : {};
                }
                return {};
            },
        },
    ];

    const emailConfirmedValue = !hasUpdatedEmail && { emailConfirmed: formData?.email };

    return (
        <SignupForm
            initialData={{ ...formData, ...emailConfirmedValue }}
            validationSchema={validationSchema}
            updates={onFormUpdate}>
            <Field.Text
                type="email"
                name="email"
                label={t`Your email address (used to create your November First account and verification code)`}
                required
            />

            <Field.Text type="email" name="emailConfirmed" label={t`Confirm email address`} required />

            <Alert state="information">
                <Trans>
                    November First will send you a confirmation code to this email address shortly. Remember to check your spam
                    folder if the email does not appear.
                </Trans>
            </Alert>

            <div className="my-3">
                <Checkbox
                    label={
                        <span>
                            <Trans>
                                Yes, please, sign up for e-conomics' newsletter, which includes contains help for setting up and
                                using Smart Pay, tips and tricks, invitations to events, as well as marketing about our accounting
                                system and related software from our app partners.
                            </Trans>
                        </span>
                    }
                    name="newsletter"
                    checked={formData?.newsletter || false}
                    onChange={onNewsletterConsentChange}
                />
            </div>

            <Field.Text name="firstName" label={t`First name`} required />

            <Field.Text name="lastName" label={t`Last name`} required />

            <Field.Text name="phone" label={t`Phone number`} required />
        </SignupForm>
    );
};
